
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Package, Image } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';

interface DialogProductSelectorProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  selectedProductId: string;
  setSelectedProductId: (id: string) => void;
  organizationProducts: any[];
  onLinkProduct: () => void;
  isLoading: boolean;
}

const DialogProductSelector: React.FC<DialogProductSelectorProps> = ({
  isOpen,
  setIsOpen,
  selectedProductId,
  setSelectedProductId,
  organizationProducts,
  onLinkProduct,
  isLoading,
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Lier à un produit</DialogTitle>
          <DialogDescription>
            Sélectionnez un produit à associer à cette vidéo
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {organizationProducts.length > 0 ? (
            <>
              <ScrollArea className="h-[400px] pr-4 -mr-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {organizationProducts.map((product) => (
                    <Card 
                      key={product.id}
                      className={`cursor-pointer transition-all border-2 hover:shadow-md ${
                        selectedProductId === product.id ? 'border-primary' : 'border-transparent'
                      }`}
                      onClick={() => setSelectedProductId(product.id)}
                    >
                      <CardContent className="p-4 flex flex-col gap-2">
                        <div className="aspect-video bg-gray-100 rounded-md overflow-hidden mb-2">
                          {product.media_urls && product.media_urls.length > 0 ? (
                            <img 
                              src={product.media_urls[0]} 
                              alt={product.name} 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400">
                              <Image className="h-12 w-12 opacity-40" />
                            </div>
                          )}
                        </div>
                        
                        <h4 className="font-medium text-base line-clamp-2">{product.name}</h4>
                        
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-sm font-medium text-primary">
                            {typeof product.price === 'number' 
                              ? new Intl.NumberFormat('fr-FR', { 
                                  style: 'currency', 
                                  currency: product.currency || 'EUR' 
                                }).format(product.price)
                              : 'Prix non défini'}
                          </span>
                          
                          {selectedProductId === product.id && (
                            <div className="h-3 w-3 rounded-full bg-primary"></div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button 
                  variant="ghost" 
                  onClick={() => setIsOpen(false)}
                  disabled={isLoading}
                >
                  Annuler
                </Button>
                <Button 
                  onClick={onLinkProduct}
                  disabled={!selectedProductId || isLoading}
                  className="gap-2"
                >
                  <Package className="h-4 w-4" />
                  {isLoading ? 'Chargement...' : 'Lier ce produit'}
                </Button>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Package className="h-16 w-16 text-gray-300 mb-4" />
              <h3 className="text-xl font-medium mb-2">Aucun produit disponible</h3>
              <p className="text-gray-500 max-w-md">
                Vous devez d'abord créer des produits dans votre boutique avant de pouvoir les associer à vos vidéos.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DialogProductSelector;
